import { create } from 'zustand'
import { indexCatalogs } from './catalog'
import type { CatalogIndex } from './catalog/types'
import { computeAll } from './domain/balance'
import { findPrereqProblems, type GraphProblem } from './domain/graph'
import { serializeQuest, questPath } from './domain/serialize'
import type { Quest } from './domain/types'
import {
  GitHubError,
  headCommit,
  loadSnapshot,
  putQuestFile,
  whoAmI,
  type Snapshot,
} from './github/client'
import { branchNameFor, proposeChanges } from './github/propose'
import { clearToken, loadToken, saveToken } from './github/token'

const DRAFTS_KEY = 'kingsmith.drafts'

interface Draft {
  quest: Quest
  /** Head commit when this draft started — lets us spot a Unity re-export underneath. */
  baseCommit: string
  savedAt: number
}

function readDrafts(): Record<string, Draft> {
  try {
    return JSON.parse(localStorage.getItem(DRAFTS_KEY) ?? '{}') as Record<string, Draft>
  } catch {
    return {}
  }
}

function writeDrafts(drafts: Record<string, Draft>): void {
  try {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts))
  } catch {
    /* over quota or private mode — the in-memory edit still stands */
  }
}

export type Status = 'idle' | 'loading' | 'ready' | 'error'

interface State {
  token: string | null
  login: string | null
  status: Status
  error: string | null

  commitSha: string | null
  quests: Map<string, { quest: Quest; sha: string }>
  broken: { path: string; error: string }[]
  catalog: CatalogIndex | null
  graphProblems: GraphProblem[]

  drafts: Record<string, Draft>
  selectedId: string | null
  saving: boolean
  /** Set when GitHub rejects a write because the file moved under us. */
  conflict: string | null
  /** Head moved since load — Unity may have re-exported. */
  remoteMoved: boolean

  signIn: (token: string) => Promise<void>
  signOut: () => void
  refresh: () => Promise<void>
  select: (id: string | null) => void
  edit: (quest: Quest) => void
  discardDraft: (id: string) => void
  save: (id: string, message: string) => Promise<void>
  proposeAll: (title: string, body: string) => Promise<{ url: string; number: number }>
  checkRemote: () => Promise<void>
}

/** The quest as currently displayed: the draft if one exists, else the committed file. */
export function currentQuest(state: State, id: string): Quest | null {
  return state.drafts[id]?.quest ?? state.quests.get(id)?.quest ?? null
}

export const useStore = create<State>((set, get) => ({
  token: loadToken(),
  login: null,
  status: 'idle',
  error: null,
  commitSha: null,
  quests: new Map(),
  broken: [],
  catalog: null,
  graphProblems: [],
  drafts: readDrafts(),
  selectedId: null,
  saving: false,
  conflict: null,
  remoteMoved: false,

  async signIn(token) {
    set({ status: 'loading', error: null })
    try {
      const user = await whoAmI(token)
      saveToken(token)
      set({ token, login: user.login })
      await get().refresh()
    } catch (error) {
      set({ status: 'error', error: error instanceof Error ? error.message : String(error) })
    }
  },

  signOut() {
    clearToken()
    set({
      token: null,
      login: null,
      status: 'idle',
      quests: new Map(),
      catalog: null,
      commitSha: null,
      selectedId: null,
    })
  },

  async refresh() {
    const token = get().token
    if (!token) return
    set({ status: 'loading', error: null })
    try {
      const snapshot: Snapshot = await loadSnapshot(token)
      const catalog = indexCatalogs(snapshot.catalogs)
      const quests = [...snapshot.quests.values()].map((entry) => entry.quest)
      set({
        commitSha: snapshot.commitSha,
        quests: snapshot.quests,
        broken: snapshot.broken,
        catalog,
        graphProblems: findPrereqProblems(quests),
        status: 'ready',
        remoteMoved: false,
      })
    } catch (error) {
      set({ status: 'error', error: error instanceof Error ? error.message : String(error) })
    }
  },

  select(id) {
    set({ selectedId: id, conflict: null })
  },

  edit(quest) {
    const { drafts, commitSha, quests, catalog } = get()
    // Keep the computed block fresh on every keystroke so the committed file matches
    // what Unity would regenerate — no spurious diff on the next export.
    const withComputed: Quest = catalog
      ? { ...quest, computed: computeAll(quest, catalog) }
      : quest

    const committed = quests.get(quest.id)?.quest
    const next = { ...drafts }
    if (committed && serializeQuest(committed) === serializeQuest(withComputed)) {
      delete next[quest.id]
    } else {
      next[quest.id] = {
        quest: withComputed,
        baseCommit: commitSha ?? '',
        savedAt: Date.now(),
      }
    }
    writeDrafts(next)
    set({ drafts: next })
  },

  discardDraft(id) {
    const next = { ...get().drafts }
    delete next[id]
    writeDrafts(next)
    set({ drafts: next })
  },

  async save(id, message) {
    const { token, drafts, quests } = get()
    if (!token) return
    const draft = drafts[id]
    if (!draft) return

    set({ saving: true, conflict: null, error: null })
    try {
      const existing = quests.get(id)
      const result = await putQuestFile(
        token,
        questPath(id),
        serializeQuest(draft.quest),
        message,
        existing?.sha ?? null,
      )
      const nextQuests = new Map(quests)
      nextQuests.set(id, { quest: draft.quest, sha: result.sha })
      const nextDrafts = { ...drafts }
      delete nextDrafts[id]
      writeDrafts(nextDrafts)
      set({
        quests: nextQuests,
        drafts: nextDrafts,
        commitSha: result.commitSha,
        saving: false,
        graphProblems: findPrereqProblems([...nextQuests.values()].map((e) => e.quest)),
      })
    } catch (error) {
      if (error instanceof GitHubError && error.isConflict) {
        set({
          saving: false,
          conflict:
            'This quest changed on GitHub since you loaded it — someone else saved, or Unity re-exported. Reload to get the current version. Your edit is kept as a draft.',
        })
      } else {
        set({ saving: false, error: error instanceof Error ? error.message : String(error) })
      }
    }
  },

  async proposeAll(title, body) {
    const { token, drafts } = get()
    if (!token) throw new Error('Not signed in.')
    const changes = Object.values(drafts).map((draft) => ({
      path: questPath(draft.quest.id),
      content: serializeQuest(draft.quest),
    }))
    if (changes.length === 0) throw new Error('No unsaved changes to propose.')
    const pull = await proposeChanges(token, branchNameFor(title), changes, title, body)
    return pull
  },

  async checkRemote() {
    const { token, commitSha } = get()
    if (!token || !commitSha) return
    try {
      const head = await headCommit(token)
      if (head !== commitSha) set({ remoteMoved: true })
    } catch {
      /* offline or transient — the banner just won't appear */
    }
  },
}))
